import React, { createContext, ReactNode, useContext } from "react";
import {
  EdgeInsets,
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

/**
 * Carries the unmodified device safe-area insets across layout layers that
 * intentionally override `SafeAreaInsetsContext` (e.g. a page wrapper that
 * zeroes the top inset because its header already consumes it).
 *
 * Modals are presented above any in-page header and need real insets to
 * position close buttons clear of the status bar / notch, so they should read
 * from this context via {@link useRealSafeAreaInsets} rather than
 * `useSafeAreaInsets` directly.
 */
export const RealSafeAreaInsetsContext = createContext<EdgeInsets | null>(null);

/**
 * Returns real device safe-area insets, falling back to the standard
 * `useSafeAreaInsets()` value when no `RealSafeAreaInsetsContext` provider is
 * present (so the hook is safe to use outside of any wrapper that overrides
 * insets).
 */
export const useRealSafeAreaInsets = (): EdgeInsets => {
  const fromContext = useContext(RealSafeAreaInsetsContext);
  const fromHook = useSafeAreaInsets();
  return fromContext ?? fromHook;
};

/**
 * Restores real device safe-area insets to the standard `SafeAreaInsetsContext`
 * for descendants. Use this around modals rendered inside a wrapper that has
 * zeroed the top inset (e.g. `PageWrapper` with the newTopbar header) so the
 * modal's close button etc. clear the status bar / notch.
 */
export const WithRealSafeAreaInsets = ({
  children,
}: {
  children: ReactNode;
}) => {
  const realInsets = useRealSafeAreaInsets();
  return (
    <SafeAreaInsetsContext.Provider value={realInsets}>
      {children}
    </SafeAreaInsetsContext.Provider>
  );
};
